import { Injectable } from '@angular/core';
import {
  StudioAnimation,
  StudioTextAnimationStep,
  WordAnimationStep,
} from '../..';
import { StudioSceneShape } from '../../../../shape/shapes/general/root/studio-scene-shape';
import {
  LineAnimation,
  TranslateAnimation,
} from '../../../../animation/text-animation.service';
import {
  TEXT_ANIMATION_PRESETS,
  TEXT_ANIMATION_PRESET_KEYS,
} from './studio-text-animation-presets';
import { StudioTextAnimationMetaService } from './studio-text-animation-meta.service';

// export interface TextAnimationMetaData {
//   baseType: ['section-appear', 'full-appear'];

//   zoom?: boolean;
//   translate?: 'left-right' | 'top-bottom';
//   disappear?: 'fade' | 'translate';
// }

// Translate directions mapped to [x, y] offsets
const TRANSLATE_FROM: Record<string, [number, number]> = {
  left: [-80, 0],
  right: [80, 0],
  top: [0, -40],
  bottom: [0, 40],
};

@Injectable({ providedIn: 'root' })
export class StudioTextAnimationService {
  constructor(private metaService: StudioTextAnimationMetaService) {}

  // Pure data descriptor — no build logic here

  textAnimations: Record<string, StudioTextAnimationStep> = {
    'zoom-out-and-letter-fade-in': {
      duration: 1.25,
      type: 'appear',
      line: {
        letter: {
          overlap: 0.2,
        },
      },
      zoom: {
        from: 0.5,
        to: 1.4,
      },
    } as StudioTextAnimationStep,
    'line-from-right': {
      duration: 1.25,
      word: {
        translate: {
          from: [0, 40],
        } as TranslateAnimation,
      } as WordAnimationStep,
      line: {
        translate: {
          from: [200, 0],
        },
      } as LineAnimation,
    },
    // Multi-line animations with lineIndex and nextLine
    'case-1': {
      duration: 1.25,
      type: 'appear',
      lineIndex: 0,
      line: {
        letter: {
          overlap: 0.85,
        },
      } as LineAnimation,
    },
    'case-2': {
      duration: 2,
      type: 'appear',
      hold: 1,
      translate: {
        from: [0, -60],
      },
    },
  };
  level1Families = ['section-appear', 'text-appear'];

  testPresets = TEXT_ANIMATION_PRESET_KEYS;
  animationPresets = TEXT_ANIMATION_PRESETS;

  getOneRandomAnimation(target: string) {
    const key = this.getRandomFromArray(this.testPresets);
    const descriptor = this.animationPresets[key];
    const animation = this.metaService.convertAnimationMetaData(
      target,
      descriptor,
    );
    console.log('animation', animation);
    return animation[0];
  }
  _getRandomAnimation(target: string) {
    const key = this.getRandomFromArray(this.testPresets);
    console.log('metaData', this.animationPresets[key]);
    return this.metaService.convertAnimationMetaData(
      target,
      this.animationPresets[key],
    );
  }

  getAnimationOptions(target: string, cnt = 3): StudioTextAnimationStep[] {
    const keys = this.selectRandomFromArray(this.testPresets, cnt);
    return keys.map(key => {
      const descriptor = this.animationPresets[key];
      console.log('[text-animation] key:', key, 'descriptor:', descriptor[0]);
      return this.metaService.convertAnimationMetaData(target, descriptor)[0];
    });
  }

  selectRandomFromArray<T>(array: T[], cnt: number): T[] {
    const shuffled = [...array].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, cnt);
  }

  /**
   * Build a multi-line animation dynamically based on the number of lines.
   * Uses lineIndex and nextLine to chain animations for each line.
   */

  getTextAnimation(sceneShape: StudioSceneShape): StudioAnimation[] {
    // In this case we assume we wil do the

    return null;

    const [textId] = sceneShape.scene.texts || [];
    const textCell = sceneShape.shapeCells[textId];

    // -- // -- //

    const baseKeys = [
      'section-appear',
      'zoom-out-and-letter-fade-in',
      'line-from-right',
    ];

    const baseKey = this.getRandomFromArray(baseKeys);

    let animation: StudioAnimation = null;
    switch (baseKey) {
      case 'section-appear':
        const innterKeys = ['by-word', 'by-letter'];
        const innerKey = this.getRandomFromArray(innterKeys);
        return this._getTextAnimation(textId, 'section-appear', innerKey);

      case 'zoom-out-and-letter-fade-in':
        animation = this.textAnimations['zoom-out-and-letter-fade-in'];
        break;
      case 'line-from-right':
        animation = this.textAnimations['line-from-right'];
        break;
    }

    animation.target = textId;
    // return animation;
  }

  _getTextAnimation(
    target: string,
    key: 'section-appear',
    innerKey: 'by-word' | 'by-letter',
  ): StudioTextAnimationStep[] {
    const animations = [];
    if (key == 'section-appear') {
      animations.push({
        target,
        duration: 1.25,
        type: 'appear',
        line: {
          segment: [0, 1],
        },
        zoom: {
          from: 2,
          to: 1.2,
        },
      } as StudioTextAnimationStep);

      if (innerKey == 'by-word') {
        animations.push({
          target,
          duration: 1.25,
          word: {
            overlap: 0.4,
          },
          line: {
            segment: [1, undefined],
            align: 'continuous',
          },
        } as StudioTextAnimationStep);
      }
      if (innerKey == 'by-letter') {
        animations.push({
          target,
          duration: 1.25,
          letter: {
            overlap: 0.9,
          },
          line: {
            segment: [1, undefined],
            align: 'continuous',
          },
        } as StudioTextAnimationStep);
      }
    }

    animations.push({
      target,
      type: 'disappear',
      duration: 0.5,
    } as StudioTextAnimationStep);

    return animations;
  }

  /**
   * Build a StudioTextAnimationStep from the TEXT_ANIMATION_OPTIONS keys.
   * familyKey: e.g. 'appear:line:translate', 'appear:line:zoom', 'appear:word', 'appear:letter'
   * subKey:    e.g. 'bottom', 'top', 'in', 'out', 'align:none', 'align:continuous'
   * option:    e.g. 'fade', 'horizontal-mask', 'by-word', 'zoom', 'blur'
   */
  buildTextAnimationStep(
    target: string,
    familyKey: string,
    subKey: string,
    option?: string,
  ): StudioTextAnimationStep | null {
    const step: StudioTextAnimationStep = {
      target,
      type: 'appear',
      duration: 1.25,
    };

    if (familyKey === 'appear:line:translate') {
      const dir = TRANSLATE_FROM[subKey];
      if (dir) {
        step.translate = { from: dir };
      }
      if (option === 'horizontal-mask' || option === 'vertical-mask-appear') {
        step.translate = { ...(step.translate || {}), mask: true } as any;
      }
      if (option === 'word') {
        step.word = { overlap: 0.4 };
      }
      if (option === 'gradient-mask') {
        step.gradientMask = 'left';
      }
      if (option === 'word-translate') {
        step.word = { overlap: 0.4, translate: { from: dir || [0, 40] } };
      }
    } else if (familyKey === 'appear:line:zoom') {
      const isIn = subKey === 'in';
      step.zoom = { from: isIn ? 0.6 : 1.4, to: 1 };
      if (option === 'horizontal-mask' || option === 'vertical-mask') {
        step.translate = { mask: true } as any;
      }
      if (option === 'by-word') {
        step.word = { overlap: 0.4 };
      }
    } else if (familyKey === 'appear:word') {
      step.word = { overlap: 0.4 };
      if (subKey.includes('continuous')) {
        step.line = { align: 'continuous' };
      } else if (subKey.includes('discrete')) {
        step.line = { align: 'discrete' };
      }
      if (option === 'zoom:out') {
        step.word.zoom = [0.6, 1];
      }
      if (option === 'translate:left') {
        step.word.translate = { from: [-40, 0] };
      }
      if (option === 'type:blur' || option === 'blur') {
        step.blur = { from: 8, to: 0, duration: 0.6 };
      }
    } else if (familyKey === 'appear:letter') {
      step.letter = { overlap: 0.85 };
      if (subKey.includes('continuous')) {
        step.line = { align: 'continuous' };
      }
      if (option === 'translate') {
        step.translate = { from: [0, 20] };
      }
      if (option === 'zoom') {
        step.zoom = { from: 0.6, to: 1 };
      }
      if (option === 'blur') {
        step.blur = { from: 8, to: 0, duration: 0.6 };
      }
    } else {
      return null;
    }

    return step;
  }

  getRandomFromArray(array: any[]) {
    const randomIndex = Math.floor(Math.random() * array.length);
    return array[randomIndex];
  }
}
